/**
 * Copyright Highway9 Networks Inc.
 */
import { useEffect, useState } from "react";
import { Button, Grid, DialogBox, DialogActions, DialogContent, DialogTitle, Typography, Input, ErrorsComponent, TimeDateInput } from "@h9/ui-lib";
import { ApiTokenService } from "~/services";
import useErrorsAndWarnings from "~/hooks/useErrorsAndWarnings";
import moment from "moment";
import { ApiToken as Token } from "~/types/ApiToken";
// import { fetchApiTokens } from "~/store/context/apiTokenSlice";
// import { useAppDispatch } from "~/store/hooks";

type Props = {
  open: boolean;
  setOpen: (open: boolean) => void;
  isEdit: boolean;
  data: Token;
};

const ApiTokenForm = ({ open, setOpen, isEdit, data }: Props) => {
  const defaultFormData = {
    name: "",
    expiry: moment().add(1, "days").unix(),
    description: "",
  } as const;
  const [formValid, setFormValid] = useState(false);
  const [formData, setFormData] = useState<typeof defaultFormData>(defaultFormData);
  const { errors, warnings, setErrorsAndWarnings, closeAlert, closeAll } = useErrorsAndWarnings();
  useEffect(() => {
    if (isEdit) {
      setFormData({
        name: data.name,
        expiry: data.expiry,
        description: data.description,
      });
    } else {
      setFormData(defaultFormData);
    }
    setErrorsAndWarnings([], []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ isEdit]);

  const handleClose = (_event?: {}, _reason?: "backdropClick" | "escapeKeyDown") => {
    setOpen(false);
  };

  // const handleRefresh = () => {
  //   setOpen(false);
  //   // If needed, add additional logic for refreshing data
  //   dispatch(fetchApiTokens());
  // };

  const handleChangeFormData = (value: string | number, key: keyof typeof formData) => {
    setFormData({
      ...formData,
      [key]: value
    });
  };

  const handleSave = async () => {
    try {
      const requestBody = {
        name: formData.name,
        expiry: formData.expiry,
        type: "apiToken",
        description: formData.description,
      };

      if (!isEdit) {
        await ApiTokenService.createApiToken(requestBody);
        setFormData(defaultFormData);
      } else {
        await ApiTokenService.updateApiToken(requestBody, data.id);
      }
      // handleRefresh();
      setOpen(false);
    } catch (error : any) {
      console.log(error);
      setErrorsAndWarnings(error.errors, error.warnings);
    }
  };

  useEffect(() => {
    let isValid = true;
    if (!formData["name"]) {
      isValid = false;
    }
    if (!formData["expiry"]) {
      isValid = false;
    }
    setFormValid(isValid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  return (
    <DialogBox onClose={handleClose} aria-labelledby="customized-dialog-title" open={open} fullWidth>
      <DialogTitle id="customized-dialog-title" onClose={handleClose}>
        <Typography variant="h6" style={{ verticalAlign: "top", fontSize: '1.1rem' }} type="heading2">
          API Token
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <ErrorsComponent errorsData={errors} warningsData={warnings} closeAll={closeAll} closeAlert={closeAlert} />

        <Grid container spacing={4}>
          <Grid xs={12}>
            <Input
              fullWidth
              label="Name"
              type="text"
              id="name"
              variant="standard"
              required
              value={formData.name}
              onChange={(e) => handleChangeFormData(e.target.value, "name")}
            />
          </Grid>
          <Grid xs={12}>
            <TimeDateInput
              label="Expiry Time"
              value={moment.unix(formData.expiry).toDate()}
              max={moment().add(90, "days").toDate()}
              min={moment().toDate()}
              disabled={isEdit || moment().isSameOrAfter(moment().add(90, "days"))}
              onChange={(date) => handleChangeFormData(moment(date).unix(), "expiry")}
            />
          </Grid>
          <Grid xs={12}>
            <Input
              fullWidth
              label="Description"
              type="text"
              id="description"
              variant="standard"
              value={formData.description}
              onChange={(e) => handleChangeFormData(e.target.value, "description")}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button autoFocus onClick={handleSave} color="primary" disabled={!formValid} variant="contained">
          Save
        </Button>
      </DialogActions>
    </DialogBox>
  );
};

export default ApiTokenForm;
